/**
 * What a location MEANS. **Pure: no `window`, no `history`, no DOM at all.**
 *
 * ┌─ ⛔ SPLIT OUT OF `routing.ts` ON 2026-08-20, BECAUSE ITS HEADER CLAIMED THIS ┐
 * │ AND THE FILE MADE IT FALSE. That header read: *"the PARSING is a pure       │
 * │ function - so the decisions are checkable in the server project without a   │
 * │ DOM, exactly like `thread-view.ts` and `connection-presentation`."*         │
 * │                                                                            │
 * │ ⚠️ IT WAS NOT. `navigate` and `currentRoute` live in the same module and    │
 * │ touch `window`, so importing the parser from the server project fails to    │
 * │ typecheck with eight `Cannot find name 'window'`. **The claim was never     │
 * │ exercised**, because until that day no test imported the router at all.     │
 * │                                                                            │
 * │ ⛔ FOUND BY WRITING THE TEST THE HEADER PROMISED WAS POSSIBLE. The comment  │
 * │ described an INTENDED architecture as an accomplished one, and nothing      │
 * │ could tell the two apart while the module had no consumer outside the       │
 * │ browser bundle. A claim with no consumer is not checked by anything.        │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * `routing.ts` re-exports everything here, so no caller needs to know about the
 * split and the browser half keeps a single import.
 */

export type Route =
  | { name: 'inbox'; conversationId: null }
  | { name: 'inbox'; conversationId: string }
  /**
   * ⛔ THE DECIDED SCHEME. `/inbox/contacts/<n>`, where `<n>` is
   * `contacts.reference` - the sequential integer staff say aloud, not the uuid.
   * Decided by Amir 2026-08-11 with the identifier strategy, ruled again
   * 2026-09-11 when its recorded premise turned out to be false.
   *
   * ⛔ IT NAMES A CONTACT AND RESOLVES TO A THREAD ON THE SERVER, because
   * "which of this contact's threads" is the conversation list's ordering and
   * a client answering it would be a second implementation of that ordering.
   * `GET /api/inbox/contacts/:reference`.
   *
   * ⚠️ THE UUID FORM ABOVE STAYS, AND IS NOT DEPRECATED YET. Every link already
   * shared, every `captureReturnTo` value stored in a live session, and the
   * list's own click handler produce it. Removing it is a separate change from
   * adding this one, and doing both at once is how a URL scheme swap breaks the
   * links it was meant to improve.
   */
  | { name: 'inbox'; contactReference: number }
  /**
   * ⛔ THE CONTACT RECORD, AND IT IS A TOP-LEVEL SECTION RATHER THAN AN INBOX
   * SUB-VIEW. §13's nav table puts Contacts beside Inbox, so `/contacts/<n>` and
   * `/inbox/contacts/<n>` are two different surfaces about the same person:
   * **the record is about the PERSON, the inbox one opens their THREAD.**
   *
   * ⚠️ Both take the same `<n>`, which is the point of the sequential identifier
   * - somebody says "fourteen four seven one" and either URL works.
   */
  | { name: 'contact'; reference: number }
  /**
   * ⛔ The contacts LIST. §13's nav puts Contacts beside Inbox, and this is its
   * index; `/contacts/<n>` is one record inside it.
   *
   * ⛔ `params` CARRIES THE SORT, FILTER AND VIEW, and it is in the route rather
   * than in component state because the self-inspection step caught it not
   * being: three captures of different list states came back **byte-identical**,
   * since the component held them in `useState` and `?sort=bookings` changed
   * nothing. ⚠️ Same argument the search page already made - a filter nobody can
   * link is a filter that exists only for the person who typed it.
   */
  | { name: 'contacts'; params: string }
  /**
   * ⛔ THE CONVERSATION CARRIES AN OPTIONAL MESSAGE, WHICH IS `Build_Order:490`:
   * *"A MESSAGE RESULT OPENS ITS CONVERSATION SCROLLED TO THAT MESSAGE,
   * highlighted."* The message id is part of the LOCATION rather than component
   * state, so the link is shareable and survives a refresh - the same argument
   * that put `conversationId` in the URL rather than in `InboxShell`.
   */
  | { name: 'inbox'; conversationId: string; messageId: string }
  /**
   * ⛔ THE PIPELINE BOARD. CRM-BUILD item 28, and Bookings is a top-level nav
   * section beside Inbox and Contacts - §13's table, and the wireframe's sub nav
   * reads Board / List / Automations.
   *
   * ⛔ `params` CARRIES THE PIPELINE AND THE VIEW, in the URL for the reason the
   * contacts list's do: *"the Snorkel pipeline, unassigned cards"* is a thing one
   * person sends another. The contacts list learned this the hard way - three
   * captures of different states came back byte-identical.
   */
  | { name: 'board'; params: string }
  /**
   * ⛔ `/inbox/bookings/<n>` - item 38's own text, verbatim, built at item 28's
   * moment because the board is its first consumer. It names a BOOKING and
   * resolves to a thread on the server through the same `conversationOrder` the
   * contact form uses. Amir 2026-09-12: *"SAME conversationOrder, NO EXCEPTION."*
   *
   * ⛔ AND ARRIVING HERE WRITES NOTHING. The booking shows in the panel for THIS
   * viewer; the shared pointer moves only on an explicit "Make active". §3.7.
   */
  | { name: 'inbox'; bookingReference: number }
  /** ⛔ Item 22. The query lives in the URL so a search can be linked and re-run. */
  | { name: 'search'; query: string }
  | { name: 'elsewhere'; path: string }

/**
 * ⛔ THE SAME UUID SHAPE `routes/media.ts` GUARDS WITH, AND FOR THE SAME REASON.
 *
 * A malformed id must never reach Postgres: `WHERE id = 'abc'` raises
 * `invalid input syntax for type uuid`, which becomes a 500 - and a 500 for
 * malformed against a 404 for absent hands back exactly the distinction the
 * uniform answer exists to withhold.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Parse a path. **Pure**, so every case is checkable without a browser.
 *
 * ⚠️ AN UNPARSEABLE ID IS `elsewhere`, NOT `inbox` WITH A NULL ID. Collapsing
 * them would silently redirect a typo'd or truncated link to the empty inbox,
 * which reads as "that conversation is gone" - a wrong answer delivered
 * confidently, and the same class as the four empty states this phase has
 * already had to separate.
 */
export function parseRoute(path: string): Route {
  /**
   * ┌─ ⛔ THE QUERY STRING IS SPLIT OFF FIRST, AND ITS ABSENCE AT THE CALLER ────┐
   * │ MADE THE SEARCH PAGE UNUSABLE. 2026-08-20, found by inspection.           │
   * │                                                                          │
   * │ `currentRoute()` passed `window.location.pathname`, which EXCLUDES the    │
   * │ query string, so `/search?q=hello` arrived as `/search` and parsed to     │
   * │ `query: ''`. The page's input is controlled by that value, so every       │
   * │ keystroke was reset to empty and no search could run at all.              │
   * │                                                                          │
   * │ ⚠️ THE PARSER ITSELF WAS NOT WRONG - the pre-fix version returns `hello`  │
   * │ for `/search?q=hello`, verified by running it. Splitting here is          │
   * │ DEFENCE, not the fix: a matcher that mangles a real URL is a trap for the │
   * │ next caller, and `/inbox?utm_source=x` is a link somebody will paste.     │
   * └──────────────────────────────────────────────────────────────────────────┘
   */
  const cut = path.indexOf('?')
  const pathname = cut === -1 ? path : path.slice(0, cut)
  const search = cut === -1 ? '' : path.slice(cut + 1)

  if (pathname === '/inbox' || pathname === '/inbox/') return { name: 'inbox', conversationId: null }

  /**
   * ⚠️ THE SEARCH PATH CARRIES ITS QUERY IN THE `q` PARAMETER, and an absent or
   * empty `q` is still the search page - it is a destination, and arriving with
   * nothing typed is the `idle` state rather than a parse failure.
   */
  if (pathname === '/search' || pathname === '/search/') {
    return { name: 'search', query: new URLSearchParams(search).get('q') ?? '' }
  }

  /**
   * ⛔ THE CONTACT FORM BEFORE THE UUID FORMS, because `/inbox/contacts/14471`
   * matches `^/inbox/([^/]+)/([^/]+)$` too. It is separated by the literal
   * segment rather than by ordering luck: `contacts` is not a uuid, so the uuid
   * guards below would reject it and it would fall through to `elsewhere` - an
   * unrecognised location for the scheme this application is supposed to use.
   *
   * ⚠️ A NON-NUMERIC OR ZERO REFERENCE IS `elsewhere`, NOT a contact route with
   * a NaN in it. Same argument as the uuid guard: an unparseable id must not
   * become a request, and `/inbox/contacts/abc` silently opening the empty inbox
   * reads as "that contact is gone".
   */
  // ⚠️ The query string travels WITH the route: the filter is the state, and a
  // state not in the URL cannot be linked, refreshed or captured.
  if (pathname === '/contacts' || pathname === '/contacts/') {
    return { name: 'contacts', params: search }
  }

  // ⚠️ Same guard as every other id here: `/contacts/abc` is `elsewhere`, not a
  // record route holding NaN.
  const record = /^\/contacts\/(\d+)\/?$/.exec(pathname)
  if (record) {
    const n = Number(record[1])
    if (Number.isSafeInteger(n) && n > 0) return { name: 'contact', reference: n }
  }

  /** ⛔ The board, with its pipeline and view travelling in the query string for
   *  the reason the contacts list's filter does. */
  if (pathname === '/bookings' || pathname === '/bookings/') {
    return { name: 'board', params: search }
  }

  const contact = /^\/inbox\/contacts\/(\d+)\/?$/.exec(pathname)
  if (contact) {
    const n = Number(contact[1])
    if (Number.isSafeInteger(n) && n > 0) return { name: 'inbox', contactReference: n }
  }

  /** ⛔ `/inbox/bookings/<n>` - item 38's text verbatim. ⚠️ BEFORE the `/inbox/
   *  <uuid>` patterns below, for the reason the message form comes first: a
   *  general pattern tested first swallows the specific one. */
  const booking = /^\/inbox\/bookings\/(\d+)\/?$/.exec(pathname)
  if (booking) {
    const n = Number(booking[1])
    if (Number.isSafeInteger(n) && n > 0) return { name: 'inbox', bookingReference: n }
  }

  // ⛔ THE MESSAGE FORM FIRST. `/inbox/<uuid>/<uuid>` also matches the shorter
  // pattern's shape at a glance, and testing the general case first would make
  // every deep link an ordinary conversation open, silently losing the scroll.
  const deep = /^\/inbox\/([^/]+)\/([^/]+)\/?$/.exec(pathname)
  if (deep && UUID.test(deep[1]!) && UUID.test(deep[2]!)) {
    return { name: 'inbox', conversationId: deep[1]!, messageId: deep[2]! }
  }

  const m = /^\/inbox\/([^/]+)\/?$/.exec(pathname)
  if (m && UUID.test(m[1]!)) return { name: 'inbox', conversationId: m[1]! }

  // ⚠️ THE FULL STRING, QUERY INCLUDED, so `routePath` round-trips whatever
  // arrived rather than silently dropping half of an unrecognised location.
  return { name: 'elsewhere', path }
}

/** The canonical path for a route, so the URL is written in one place. */
export function routePath(route: Route): string {
  if (route.name === 'elsewhere') return route.path
  if (route.name === 'search') {
    // ⚠️ `/search` WITH NO PARAMETER when the query is empty, rather than
    // `/search?q=`. The second is a different string for the same state, and two
    // spellings of one location is how a Back button starts repeating itself.
    return route.query === '' ? '/search' : `/search?q=${encodeURIComponent(route.query)}`
  }
  // ⛔ BEFORE the `conversationId === null` test below: a contact route has no
  // `conversationId` property at all, so `route.conversationId === null` is
  // `undefined === null`, which is false, and it would fall through to the
  // template below and emit `/inbox/undefined`.
  if (route.name === 'contacts') return route.params === '' ? '/contacts' : `/contacts?${route.params}`
  if (route.name === 'contact') return `/contacts/${route.reference}`
  /** ⚠️ ALSO BEFORE THE `conversationId === null` TEST, for the reason the
   *  contact routes are: a board route has no `conversationId` property, so the
   *  comparison would be `undefined === null` and fall through to `/inbox/…`. */
  if (route.name === 'board') return route.params === '' ? '/bookings' : `/bookings?${route.params}`
  if ('contactReference' in route) return `/inbox/contacts/${route.contactReference}`
  if ('bookingReference' in route) return `/inbox/bookings/${route.bookingReference}`
  if (route.conversationId === null) return '/inbox'
  return 'messageId' in route
    ? `/inbox/${route.conversationId}/${route.messageId}`
    : `/inbox/${route.conversationId}`
}
